import {
  ResponseTimeOperator,
  ResponseType,
  type ResponseTimeCondition as ResponseTimeConditionType,
} from "@redotech/redo-flows-common/rules-v3/schemas";
import { TimeUnit } from "@redotech/redo-flows-common/rules-v3/types";
import { t, useTranslation } from "@redotech/redo-i18n";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useMemo } from "react";

const responseTypeOptions = () => [
  {
    id: ResponseType.MERCHANT,
    type: "text" as const,
    text: t("Merchant response"),
    value: ResponseType.MERCHANT,
  },
  {
    id: ResponseType.CUSTOMER,
    type: "text" as const,
    text: t("Customer response"),
    value: ResponseType.CUSTOMER,
  },
  {
    id: ResponseType.EITHER,
    type: "text" as const,
    text: t("Any response"),
    value: ResponseType.EITHER,
  },
];

const operatorOptions = () => [
  {
    id: ResponseTimeOperator.MORE_THAN,
    type: "text" as const,
    text: t("More than", { context: "comparison operator" }),
    value: ResponseTimeOperator.MORE_THAN,
  },
  {
    id: ResponseTimeOperator.LESS_THAN,
    type: "text" as const,
    text: t("Less than", { context: "comparison operator" }),
    value: ResponseTimeOperator.LESS_THAN,
  },
  {
    id: ResponseTimeOperator.BETWEEN,
    type: "text" as const,
    text: t("Between", { context: "comparison operator for a range" }),
    value: ResponseTimeOperator.BETWEEN,
  },
];

const unitOptions = () => [
  {
    id: TimeUnit.MINUTES,
    type: "text" as const,
    text: t("minutes", { context: "time unit" }),
    value: TimeUnit.MINUTES,
  },
  {
    id: TimeUnit.HOURS,
    type: "text" as const,
    text: t("hours", { context: "time unit" }),
    value: TimeUnit.HOURS,
  },
  {
    id: TimeUnit.DAYS,
    type: "text" as const,
    text: t("days", { context: "time unit" }),
    value: TimeUnit.DAYS,
  },
];

export const ResponseTimeCondition = memo(function ResponseTimeCondition({
  condition,
  onChange,
  editableFields,
}: {
  condition: ResponseTimeConditionType;
  onChange: (updated: ResponseTimeConditionType) => void;
  editableFields?: ReadonlySet<string>;
}) {
  const { t } = useTranslation();
  const isEditable = (f: string) =>
    editableFields === undefined || editableFields.has(f);
  const responseTypeEditable = isEditable("responseType");
  const operatorEditable = isEditable("responseTimeOperator");
  const valueEditable = isEditable("value");
  const minMaxEditable = isEditable("minValue") || isEditable("maxValue");
  const unitEditable = isEditable("unit");

  const selectedResponseType = useMemo(
    () =>
      responseTypeOptions().find((opt) => opt.value === condition.responseType),
    [condition.responseType],
  );

  const selectedOperator = useMemo(
    () =>
      operatorOptions().find(
        (opt) => opt.value === condition.responseTimeOperator,
      ),
    [condition.responseTimeOperator],
  );

  const selectedUnit = useMemo(
    () => unitOptions().find((opt) => opt.value === condition.unit),
    [condition.unit],
  );

  return (
    <Flex dir="column" gap="md" w="full">
      <RedoSingleSelectDropdownInput
        disabled={!responseTypeEditable}
        options={responseTypeOptions()}
        optionSelected={({ item }) => {
          if (!responseTypeEditable) {
            return;
          }
          onChange({ ...condition, responseType: item.value });
        }}
        placeholder={t("Select response type")}
        selectedItem={selectedResponseType}
      />
      <RedoSingleSelectDropdownInput
        disabled={!operatorEditable}
        options={operatorOptions()}
        optionSelected={({ item }) => {
          if (!operatorEditable) {
            return;
          }
          const base = {
            conditionType: condition.conditionType,
            responseType: condition.responseType,
            unit: condition.unit,
          } as const;
          if (item.value === ResponseTimeOperator.BETWEEN) {
            onChange({
              ...base,
              responseTimeOperator: item.value,
              minValue: 0,
              maxValue: 0,
            });
          } else {
            onChange({ ...base, responseTimeOperator: item.value, value: 0 });
          }
        }}
        placeholder={t("Select operator")}
        selectedItem={selectedOperator}
      />
      {(condition.responseTimeOperator === ResponseTimeOperator.MORE_THAN ||
        condition.responseTimeOperator === ResponseTimeOperator.LESS_THAN) && (
        <Flex align="center" gap="sm">
          <RedoTextInput
            inputTypeHint="number"
            min={0}
            placeholder="0"
            setValue={(val: string) => {
              if (!valueEditable) {
                return;
              }
              const num = parseFloat(val);
              onChange({ ...condition, value: isNaN(num) ? 0 : num });
            }}
            size="sm"
            state={valueEditable ? "default" : "readonly"}
            value={condition.value.toString()}
          />
          <RedoSingleSelectDropdownInput
            disabled={!unitEditable}
            options={unitOptions()}
            optionSelected={({ item }) => {
              if (!unitEditable) {
                return;
              }
              onChange({ ...condition, unit: item.value });
            }}
            selectedItem={selectedUnit}
          />
        </Flex>
      )}
      {condition.responseTimeOperator === ResponseTimeOperator.BETWEEN && (
        <Flex align="center" gap="sm">
          <RedoTextInput
            inputTypeHint="number"
            min={0}
            placeholder="0"
            setValue={(val: string) => {
              if (!minMaxEditable) {
                return;
              }
              const num = parseFloat(val);
              onChange({ ...condition, minValue: isNaN(num) ? 0 : num });
            }}
            size="sm"
            state={minMaxEditable ? "default" : "readonly"}
            value={condition.minValue.toString()}
          />
          <Text fontSize="sm" textColor="secondary">
            {t("and", { context: "conjunction between two range values" })}
          </Text>
          <RedoTextInput
            inputTypeHint="number"
            min={0}
            placeholder="0"
            setValue={(val: string) => {
              if (!minMaxEditable) {
                return;
              }
              const num = parseFloat(val);
              onChange({ ...condition, maxValue: isNaN(num) ? 0 : num });
            }}
            size="sm"
            state={minMaxEditable ? "default" : "readonly"}
            value={condition.maxValue.toString()}
          />
          <RedoSingleSelectDropdownInput
            disabled={!unitEditable}
            options={unitOptions()}
            optionSelected={({ item }) => {
              if (!unitEditable) {
                return;
              }
              onChange({ ...condition, unit: item.value });
            }}
            selectedItem={selectedUnit}
          />
        </Flex>
      )}
    </Flex>
  );
});
